import { useEffect, useMemo } from 'react'
import type { ReactElement } from 'react'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import HomePage from '@/pages/home'
import VideoConverterPage from '@/pages/tools/video-converter'
import AudioConverterPage from '@/pages/tools/audio-converter'
import ImageCompressorPage from '@/pages/tools/image-compressor'
import ZipToolsPage from '@/pages/tools/zip'
import NotFoundPage from '@/pages/not-found'
import { I18nContext, translate } from '@/lib/i18n'
import {
  detectBrowserLang,
  isLang,
  LANGS,
  langPrefixPath,
  stripLangPrefix,
  writeLangCookie,
  type Lang,
} from '@/lib/lang'

/**
 * Every app route, registered once per language prefix. A flat (non-nested)
 * route table is intentional: react-router v7 nested <Routes> lose the
 * relative context of an outer optional segment (":lang?"), so "/en/tools/zip"
 * would not match a nested "tools/zip" branch.
 */
const APP_ROUTES: readonly { path: string; element: ReactElement }[] = [
  { path: '/', element: <HomePage /> },
  { path: '/tools/video-converter', element: <VideoConverterPage /> },
  { path: '/tools/audio-converter', element: <AudioConverterPage /> },
  { path: '/tools/image-compressor', element: <ImageCompressorPage /> },
  { path: '/tools/zip', element: <ZipToolsPage /> },
]

/** Scrolls to #hash anchors after navigation; resets to top otherwise. */
function ScrollToHash() {
  const { hash, pathname } = useLocation()

  useEffect(() => {
    if (hash) {
      document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' })
    } else {
      window.scrollTo(0, 0)
    }
  }, [hash, pathname])

  return null
}

export default function App() {
  const location = useLocation()
  const navigate = useNavigate()

  // Language comes from the URL's first segment; anything else falls back to
  // the browser locale (production always carries a prefix via the middleware).
  const firstSegment = location.pathname.split('/')[1] ?? ''
  const lang: Lang = isLang(firstSegment) ? firstSegment : detectBrowserLang()

  const value = useMemo(
    () => ({
      lang,
      t: (key: Parameters<typeof translate>[0], params?: Record<string, string | number>) =>
        translate(key, lang, params),
      path: (to: string) => langPrefixPath(to, lang),
      switchLang: (next: Lang) => {
        const base = stripLangPrefix(location.pathname)
        navigate(`${langPrefixPath(base, next)}${location.search}${location.hash}`)
        writeLangCookie(next)
      },
    }),
    [lang, location.pathname, location.search, location.hash, navigate],
  )

  return (
    <I18nContext.Provider value={value}>
      <ScrollToHash />
      <Routes>
        {APP_ROUTES.flatMap((route) =>
          LANGS.map((candidate) => (
            <Route
              key={`${candidate}${route.path}`}
              path={langPrefixPath(route.path, candidate)}
              element={route.element}
            />
          )),
        )}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </I18nContext.Provider>
  )
}